import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Send, Users, Eye, AlertTriangle, CheckCircle,
  Layout, FileText, Megaphone, Newspaper, Briefcase, Sparkles
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template icons mapping
const templateIcons = {
  announcement: Megaphone,
  promotion: Sparkles,
  newsletter: Newspaper,
  partner_update: Briefcase,
  new_service: FileText
};

export default function AdminCampaignForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewingAudience, setPreviewingAudience] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [useTemplate, setUseTemplate] = useState(true);
  const [renderingTemplate, setRenderingTemplate] = useState(false);

  const [formData, setFormData] = useState({
    subject: '',
    content_html: '',
    audience_type: 'all_users',
    audience_filters: {}
  });

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Fetch existing campaign for edit
  useEffect(() => {
    if (isEdit) {
      fetchCampaign();
      setUseTemplate(false); // Switch to raw HTML mode for editing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          subject: data.campaign.subject,
          content_html: data.campaign.content_html,
          audience_type: data.campaign.audience_type,
          audience_filters: data.campaign.audience_filters || {}
        });
      }
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    
    // Initialize variables with empty values
    const initialVars = {};
    template.variables.forEach(v => {
      initialVars[v] = '';
    });
    setTemplateVariables(initialVars);
    setFormData(prev => ({ ...prev, content_html: '' }));
  };

  const handleTemplateVariableChange = (key, value) => {
    setTemplateVariables(prev => ({ ...prev, [key]: value }));
  };

  const handleRenderTemplate = async () => {
    if (!selectedTemplate) return;
    
    setRenderingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/templates/${selectedTemplate.id}/render`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateVariables)
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, content_html: data.html }));
        toast({
          title: "Template Rendered",
          description: "Your email content has been generated from the template.",
        });
      }
    } catch (error) {
      console.error('Failed to render template:', error);
      toast({
        title: "Error",
        description: "Failed to render template",
        variant: "destructive"
      });
    } finally {
      setRenderingTemplate(false);
    }
  };

  const handlePreviewAudience = async () => {
    setPreviewingAudience(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/preview-audience`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          audience_type: formData.audience_type,
          filters: formData.audience_filters
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAudiencePreview(data);
      }
    } catch (error) {
      console.error('Failed to preview audience:', error);
    } finally {
      setPreviewingAudience(false);
    }
  };

  const handleSave = async (andSend = false) => {
    if (!formData.subject.trim()) {
      toast({
        title: "Error",
        description: "Subject is required",
        variant: "destructive"
      });
      return;
    }

    if (!formData.content_html.trim()) {
      toast({
        title: "Error",
        description: "Email content is required. Please fill in the template or write HTML.",
        variant: "destructive"
      });
      return;
    }

    if (andSend) {
      setSending(true);
    } else {
      setSaving(true);
    }

    try {
      const token = localStorage.getItem('token');
      
      // Create or update campaign
      const saveResponse = await fetch(
        isEdit 
          ? `${API_URL}/api/admin/campaigns/${id}`
          : `${API_URL}/api/admin/campaigns`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Failed to save campaign');
      }

      const saveData = await saveResponse.json();
      const campaignId = isEdit ? id : saveData.campaign_id;

      if (andSend) {
        // Send the campaign
        const sendResponse = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}/send`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!sendResponse.ok) {
          const error = await sendResponse.json();
          throw new Error(error.detail || 'Failed to send campaign');
        }

        toast({
          title: "Campaign Sending",
          description: "Your email campaign is being sent in the background.",
        });
      } else {
        toast({
          title: "Saved",
          description: isEdit ? "Campaign updated" : "Campaign saved as draft",
        });
      }

      navigate('/admin/campaigns');
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  const audienceOptions = [
    { value: 'all_users', label: 'All Registered Users', description: 'Every user who has an account' },
    { value: 'partners_only', label: 'Partners Only', description: 'Approved agency partners' },
    { value: 'customers_with_orders', label: 'Customers with Orders', description: 'Users who have placed at least one order' }
  ];

  const getVariableLabel = (key) => {
    const labels = {
      title: 'Headline / Title',
      subtitle: 'Subtitle',
      body: 'Main Content',
      intro: 'Introduction',
      cta_text: 'Button Text',
      cta_url: 'Button Link (URL)',
      highlight_text: 'Highlight Text',
      service_name: 'Service Name',
      features: 'Features (use <br> for line breaks)',
      section1_title: 'Section 1 Title',
      section1_body: 'Section 1 Content',
      section2_title: 'Section 2 Title',
      section2_body: 'Section 2 Content',
      footer_text: 'Footer Text'
    };
    return labels[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  };

  const getVariablePlaceholder = (key) => {
    const placeholders = {
      title: 'e.g., Exciting News from BusinessMint PR',
      subtitle: 'e.g., Limited Time Offer',
      body: 'Your main message here...',
      intro: 'Brief introduction to your newsletter...',
      cta_text: 'e.g., Learn More, Shop Now, Get Started',
      cta_url: 'https://businessmintpr.com/...',
      highlight_text: 'e.g., Save 20% on your next order!',
      service_name: 'e.g., Social Media Marketing Package',
      features: 'Feature 1<br>Feature 2<br>Feature 3',
      section1_title: 'e.g., This Month\'s Highlights',
      section1_body: 'Content for section 1...',
      section2_title: 'e.g., Upcoming Events',
      section2_body: 'Content for section 2...'
    };
    return placeholders[key] || '';
  };

  const isLargeField = (key) => {
    return ['body', 'intro', 'features', 'section1_body', 'section2_body'].includes(key);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center">
          <p className="text-[#737373]">Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/campaigns')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0d0d0d]">
              {isEdit ? 'Edit Campaign' : 'New Email Campaign'}
            </h1>
            <p className="text-[#737373] text-sm">
              {useTemplate ? 'Choose a template and customize it' : 'Write your email content'}
            </p>
          </div>
        </div>

        {/* Template/Raw Toggle - only for new campaigns */}
        {!isEdit && (
          <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-[#e5e5e5]">
            <button
              onClick={() => setUseTemplate(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                useTemplate 
                  ? 'bg-[#D4AF37] text-[#0d0d0d] font-medium' 
                  : 'bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5]'
              }`}
            >
              <Layout className="w-4 h-4" />
              Use Template
            </button>
            <button
              onClick={() => setUseTemplate(false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                !useTemplate 
                  ? 'bg-[#D4AF37] text-[#0d0d0d] font-medium' 
                  : 'bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5]'
              }`}
            >
              <FileText className="w-4 h-4" />
              Write HTML
            </button>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg border border-[#e5e5e5] p-6 space-y-6">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-[#0d0d0d] mb-2">
              Email Subject *
            </label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g., Exciting News from BusinessMint PR"
              className="w-full"
              data-testid="campaign-subject"
            />
          </div>

          {/* Template Selection */}
          {useTemplate && !isEdit && (
            <div>
              <label className="block text-sm font-medium text-[#0d0d0d] mb-3">
                Select Template *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {templates.map((template) => {
                  const Icon = templateIcons[template.id] || FileText;
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all text-center ${
                        selectedTemplate?.id === template.id
                          ? 'border-[#D4AF37] bg-[#D4AF37]/5 ring-2 ring-[#D4AF37]/20'
                          : 'border-[#e5e5e5] hover:border-[#D4AF37]/50'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-lg flex items-center justify-center ${
                        selectedTemplate?.id === template.id ? 'bg-[#D4AF37]' : 'bg-[#f5f5f5]'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          selectedTemplate?.id === template.id ? 'text-[#0d0d0d]' : 'text-[#737373]'
                        }`} />
                      </div>
                      <p className="font-medium text-sm text-[#0d0d0d]">{template.name}</p>
                      <p className="text-xs text-[#737373] mt-1 line-clamp-2">{template.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Template Variables Form */}
          {useTemplate && selectedTemplate && !isEdit && (
            <div className="border border-[#e5e5e5] rounded-lg p-5 bg-[#fafafa]">
              <h3 className="font-medium text-[#0d0d0d] mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                Customize Your {selectedTemplate.name}
              </h3>
              <div className="space-y-4">
                {selectedTemplate.variables.map((varKey) => (
                  <div key={varKey}>
                    <label className="block text-sm font-medium text-[#525252] mb-1">
                      {getVariableLabel(varKey)}
                    </label>
                    {isLargeField(varKey) ? (
                      <textarea
                        value={templateVariables[varKey] || ''}
                        onChange={(e) => handleTemplateVariableChange(varKey, e.target.value)}
                        placeholder={getVariablePlaceholder(varKey)}
                        rows={4}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                      />
                    ) : (
                      <Input
                        value={templateVariables[varKey] || ''}
                        onChange={(e) => handleTemplateVariableChange(varKey, e.target.value)}
                        placeholder={getVariablePlaceholder(varKey)}
                        className="w-full"
                      />
                    )}
                  </div>
                ))}
              </div>
              <Button
                onClick={handleRenderTemplate}
                disabled={renderingTemplate}
                className="mt-4 bg-[#0d0d0d] hover:bg-[#262626] text-white"
              >
                {renderingTemplate ? 'Generating...' : 'Generate Email Content'}
              </Button>
            </div>
          )}

          {/* Raw HTML Editor (shown when not using template or after template is rendered) */}
          {(!useTemplate || formData.content_html || isEdit) && (
            <div>
              <label className="block text-sm font-medium text-[#0d0d0d] mb-2">
                Email Content (HTML) {!useTemplate && '*'}
              </label>
              <textarea
                value={formData.content_html}
                onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                placeholder={!useTemplate ? `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1 style="color: #D4AF37;">Your Headline Here</h1>
  <p>Your message content...</p>
  <a href="https://businessmintpr.com" style="background: #D4AF37; color: #0d0d0d; padding: 12px 24px; text-decoration: none; display: inline-block;">
    Call to Action
  </a>
</div>` : 'Template HTML will appear here after generation...'}
                rows={useTemplate ? 8 : 15}
                className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                data-testid="campaign-content"
              />
              {useTemplate && formData.content_html && (
                <p className="text-xs text-[#737373] mt-2">
                  You can edit the generated HTML above to make final adjustments.
                </p>
              )}
            </div>
          )}

          {/* Audience Selection */}
          <div>
            <label className="block text-sm font-medium text-[#0d0d0d] mb-2">
              Audience *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {audienceOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    setFormData({ ...formData, audience_type: option.value });
                    setAudiencePreview(null);
                  }}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    formData.audience_type === option.value
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-[#e5e5e5] hover:border-[#D4AF37]/50'
                  }`}
                  data-testid={`audience-${option.value}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                      formData.audience_type === option.value
                        ? 'border-[#D4AF37] bg-[#D4AF37]'
                        : 'border-[#d4d4d4]'
                    }`}>
                      {formData.audience_type === option.value && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#0d0d0d]">{option.label}</p>
                      <p className="text-xs text-[#737373] mt-1">{option.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview Audience Button */}
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviewAudience}
                disabled={previewingAudience}
              >
                <Users className="w-4 h-4 mr-2" />
                {previewingAudience ? 'Loading...' : 'Preview Audience'}
              </Button>

              {audiencePreview && (
                <div className="mt-4 p-4 bg-[#f5f5f5] rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#0d0d0d]">{audiencePreview.total_recipients}</p>
                      <p className="text-xs text-[#737373]">Total Recipients</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{audiencePreview.eligible_count}</p>
                      <p className="text-xs text-[#737373]">Eligible to Receive</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{audiencePreview.rate_limited_count}</p>
                      <p className="text-xs text-[#737373]">Rate Limited</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{audiencePreview.unsubscribed_count}</p>
                      <p className="text-xs text-[#737373]">Unsubscribed</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          {formData.content_html && (
            <div>
              <label className="block text-sm font-medium text-[#0d0d0d] mb-2">
                <Eye className="w-4 h-4 inline mr-2" />
                Email Preview
              </label>
              <div className="border border-[#e5e5e5] rounded-lg overflow-hidden bg-[#f5f5f5]">
                <div className="bg-[#0d0d0d] px-4 py-2 text-xs text-[#737373]">
                  Preview - Actual email may vary slightly in different email clients
                </div>
                <div className="p-4">
                  <iframe
                    srcDoc={formData.content_html}
                    className="w-full h-[500px] bg-white rounded border border-[#e5e5e5]"
                    title="Email Preview"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Rate Limit Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Before You Send</p>
              <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                <li>Max 2 emails per user per week (rate limited users will be skipped)</li>
                <li>Unsubscribed users are automatically excluded</li>
                <li>An unsubscribe link is automatically added to all emails</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/campaigns')}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving || sending}
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving || sending}
            className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]"
            data-testid="send-campaign-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Save & Send'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
